import { Check, ChevronRight, GraduationCap, Sparkles } from "lucide-react";
import ReactMarkdown from "react-markdown";
import { StudyWorkshop } from "../features/workshop/StudyWorkshop";
import { PPCExplorer } from "../features/diagrams/PPCExplorer";
import { MicroDiagram } from "../features/diagrams/MicroDiagram";
import { syllabusTitles as sectionTitles } from "../features/navigation/directory";
import type { KnowledgePoint, Locale } from "../types/content";

interface KnowledgeArticleProps {
  point: KnowledgePoint;
  locale: Locale;
  showHl?: boolean;
  labels: {
    keyConclusion: string;
    definition: string;
    mechanism: string;
    explanation: string;
    application: string;
    misconception: string;
    exam: string;
    summary: string;
    previous: string;
    next: string;
    knowledgePoint: string;
  };
  onPrevious: (() => void) | undefined;
  onNext: (() => void) | undefined;
}

const levelLabels = {
  core: "CORE · SL / HL",
  hl: "HL",
  extension: "EXTENSION",
};

export function KnowledgeArticle({ point, locale, labels, onPrevious, onNext, showHl = true }: KnowledgeArticleProps) {
  const content = point.content[locale];
  const otherLocale: Locale = locale === "zh-CN" ? "en" : "zh-CN";
  const numericId = point.meta.id.match(/^u\d+-(\d+)/)?.[1] ?? "—";

  return (
    <article className="knowledge-article">
      <nav className="breadcrumbs" aria-label="Breadcrumb">
        <span>Unit {point.meta.section.split(".")[0]}</span>
        <ChevronRight size={13} />
        <span>{sectionTitles[point.meta.section]?.[locale] ?? point.meta.section}</span>
        <ChevronRight size={13} />
        <strong>{content.title}</strong>
      </nav>

      <header className="article-header">
        <div className="article-kicker">
          <span className={`level-badge level-${point.meta.level}`}>{levelLabels[point.meta.level]}</span>
          <span>{labels.knowledgePoint} {numericId}</span>
        </div>
        <h1>{content.title}</h1>
        <p className="english-term">{point.content[otherLocale].title}</p>
        {point.workshop && <button type="button" className="workshop-entry" onClick={() => document.getElementById("study-workshop")?.scrollIntoView({ behavior: "smooth" })}>{locale === "zh-CN" ? "进入关税学习工坊：画图、考试题与反馈 ↓" : "Open the tariff workshop: drawing, exam tasks and feedback ↓"}</button>}
      </header>

      <section className="takeaway-card" aria-labelledby="takeaway-title">
        <span className="takeaway-icon" aria-hidden="true"><Sparkles size={19} /></span>
        <div>
          <h2 id="takeaway-title">{labels.keyConclusion}</h2>
          <p>{content.takeaway}</p>
        </div>
      </section>

      <section className="article-section" aria-labelledby="definition-title">
        <p className="section-number">01</p>
        <div>
          <h2 id="definition-title">{labels.definition}</h2>
          <p className="lead-definition">{content.definition}</p>
        </div>
      </section>

      <section className="article-section" aria-labelledby="mechanism-title">
        <p className="section-number">02</p>
        <div>
          <h2 id="mechanism-title">{labels.mechanism}</h2>
          <ol className="causal-chain">
            {content.causalChain.map((step, index) => (
              <li key={step}>
                <span>{String(index + 1).padStart(2, "0")}</span>
                <p>{step}</p>
                {index < content.causalChain.length - 1 && <ChevronRight size={16} aria-hidden="true" />}
              </li>
            ))}
          </ol>
        </div>
      </section>

      <section className="article-section prose-section" aria-labelledby="explanation-title">
        <p className="section-number">03</p>
        <div>
          <h2 id="explanation-title">{labels.explanation}</h2>
          <ReactMarkdown>{content.explanation}</ReactMarkdown>
        </div>
      </section>

      {showHl && content.hlExtension && <section className="article-section prose-section" aria-labelledby="hl-extension-title">
        <div>
          <h2 id="hl-extension-title">{locale === "zh-CN" ? "HL 深入与计算" : "HL detail and calculations"}</h2>
          <ReactMarkdown>{content.hlExtension}</ReactMarkdown>
        </div>
      </section>}

      {point.meta.diagramIds.includes("ppc-core") && <PPCExplorer locale={locale} />}
      {point.meta.diagramIds.filter((id) => id !== "ppc-core").map((id) => <MicroDiagram key={id} id={id} locale={locale} />)}

      {content.realWorldExample && (
        <section className="article-section" aria-labelledby="application-title">
          <p className="section-number">04</p>
          <div>
            <h2 id="application-title">{labels.application}</h2>
            <div className="case-card">
              <div className="case-meta"><span>CASE NOTE</span><time>{content.realWorldExample.observedAt}</time></div>
              <h3>{content.realWorldExample.title}</h3>
              <p>{content.realWorldExample.explanation}</p>
              <a href={content.realWorldExample.sourceUrl} target="_blank" rel="noreferrer">
                {locale === "zh-CN" ? "查看外部资料来源" : "Open external source"} <ChevronRight size={14} />
              </a>
            </div>
          </div>
        </section>
      )}

      <div className="two-up-sections">
        <section className="study-note misconception-note">
          <span className="note-label">{labels.misconception}</span>
          <ul>{content.misconceptions.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
        <section className="study-note exam-note">
          <span className="note-label">{labels.exam}</span>
          <ul>{content.examApplication.map((item) => <li key={item}>{item}</li>)}</ul>
        </section>
      </div>

      <section className="summary-card" aria-labelledby="summary-title">
        <div>
          <span className="summary-mark"><GraduationCap size={20} /></span>
          <h2 id="summary-title">{labels.summary}</h2>
        </div>
        <ul>
          {content.summary.map((item) => <li key={item}><Check size={15} /><span>{item}</span></li>)}
        </ul>
      </section>

      {point.workshop && <StudyWorkshop key={point.meta.id} pointId={point.meta.id} content={point.workshop} locale={locale} showHl={showHl} />}

      <nav className="article-pagination" aria-label="Knowledge navigation">
        <button type="button" onClick={onPrevious} disabled={!onPrevious}><span>←</span><small>{labels.previous}</small></button>
        <button type="button" onClick={onNext} disabled={!onNext}><small>{labels.next}</small><span>→</span></button>
      </nav>
    </article>
  );
}
